/** Max display width for reader thumbnails — keeps decoded bitmap RAM low on iPad. */
export const READER_IMAGE_MAX_WIDTH = 720;

/**
 * Downscale a raster blob to maxW px wide (aspect preserved). Preserves alpha via PNG.
 * Used only in the speaker reader; editor/export keep full-res blobs.
 */
export async function downscaleImageBlob(blob: Blob, maxW: number): Promise<Blob> {
  if (maxW <= 0) return blob;

  const bitmap = await createImageBitmap(blob);
  try {
    if (bitmap.width <= maxW) {
      return blob;
    }

    const scale = maxW / bitmap.width;
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const hasAlpha = blob.type === "image/png" || blob.type === "image/webp";
    const mime = hasAlpha ? "image/png" : "image/jpeg";
    const quality = hasAlpha ? undefined : 0.88;

    if (typeof OffscreenCanvas !== "undefined") {
      const canvas = new OffscreenCanvas(w, h);
      const ctx = canvas.getContext("2d");
      if (!ctx) return blob;
      ctx.drawImage(bitmap, 0, 0, w, h);
      return canvas.convertToBlob({ type: mime, quality });
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return blob;
    ctx.drawImage(bitmap, 0, 0, w, h);
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (out) => (out ? resolve(out) : reject(new Error("toBlob failed"))),
        mime,
        quality,
      );
    });
  } finally {
    bitmap.close();
  }
}
